import { useCallback, useEffect, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	IconButton,
	InputLabel,
	Link,
	MenuItem,
	Select,
	Switch,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Tabs,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { Add, ContentCopy, DeleteOutline, Key } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import tokenService from "../services/tokenService";
import { elapsedMs, formatDate, timeAgo } from "../utils/dateUtils";
import { CI_PROVIDERS, ciSnippet } from "../utils/ciSnippets";

// Settings → API tokens: personal tokens for CI pipelines (they only reach /api/ci; see cli/README.md).

const EXPIRY_OPTIONS = [
	{ value: 30, label: "30 days" },
	{ value: 90, label: "90 days" },
	{ value: 365, label: "1 year" },
	{ value: "never", label: "Never" },
];

const errorMessage = (err, fallback) => err.response?.data?.message || fallback;

const mono = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace" };

function CopyButton({ text, label = "Copy" }) {
	const { enqueueSnackbar } = useSnackbar();
	return (
		<Button
			size="small"
			startIcon={<ContentCopy fontSize="small" />}
			onClick={async () => {
				try {
					await navigator.clipboard.writeText(text);
					enqueueSnackbar("Copied", { variant: "success" });
				} catch {
					enqueueSnackbar("Couldn't copy: select the text and copy it", { variant: "warning" });
				}
			}}
		>
			{label}
		</Button>
	);
}

function CodeBlock({ children }) {
	return (
		<Box
			component="pre"
			sx={{
				...mono,
				m: 0,
				p: 1.5,
				fontSize: 12.5,
				lineHeight: 1.55,
				overflowX: "auto",
				borderRadius: 1,
				border: 1,
				borderColor: "divider",
				bgcolor: "action.hover",
				color: "text.primary",
				whiteSpace: "pre",
			}}
		>
			{children}
		</Box>
	);
}

/** How to use a token in a pipeline, filled with this server's address. */
export function PipelineSetup() {
	const [provider, setProvider] = useState("github");
	const origin = window.location.origin;
	const snippet = ciSnippet(provider, origin);
	const current = CI_PROVIDERS.find((p) => p.id === provider);
	return (
		<Box>
			<Tabs value={provider} onChange={(_, v) => setProvider(v)} variant="scrollable" sx={{ mb: 1.5, minHeight: 36 }}>
				{CI_PROVIDERS.map((p) => (
					<Tab key={p.id} value={p.id} label={p.label} sx={{ minHeight: 36, py: 0.5 }} />
				))}
			</Tabs>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
				1. Store the token as a secret: {current.secretHint}.
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
				2. Add this to your pipeline, with your suite's name or id in place of <code>Smoke</code>:
			</Typography>
			<CodeBlock>{snippet}</CodeBlock>
			<Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}>
				<CopyButton text={snippet} />
			</Box>
			<Typography variant="caption" color="text.secondary" component="p">
				The job needs Node.js 18 or newer. The build fails when a test fails (exit code 1); the JUnit report is written either way.
				If this server uses a self-signed certificate, add <code>-k</code> to <code>curl</code> and <code>--insecure</code> to the
				command, or point <code>NODE_EXTRA_CA_CERTS</code> at its certificate. All options:{" "}
				<code>node testify.mjs --help</code>.
			</Typography>
		</Box>
	);
}

function CreateDialog({ open, onClose, onCreated }) {
	const { enqueueSnackbar } = useSnackbar();
	const [name, setName] = useState("");
	const [expiry, setExpiry] = useState(90);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (open) {
			setName("");
			setExpiry(90);
		}
	}, [open]);

	const submit = async (e) => {
		e.preventDefault();
		setSaving(true);
		try {
			const created = await tokenService.create(name.trim(), expiry === "never" ? null : expiry);
			onCreated(created);
		} catch (err) {
			enqueueSnackbar(errorMessage(err, "Couldn't create the token"), { variant: "error" });
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
			<form onSubmit={submit}>
				<DialogTitle>New API token</DialogTitle>
				<DialogContent>
					<TextField
						autoFocus
						fullWidth
						label="Name"
						placeholder="e.g. GitHub Actions – web app"
						helperText="Where it's used, so you know which one to revoke."
						value={name}
						onChange={(e) => setName(e.target.value)}
						inputProps={{ maxLength: 60 }}
						required
						sx={{ mt: 1, mb: 2 }}
					/>
					<FormControl fullWidth>
						<InputLabel id="token-expiry">Expires after</InputLabel>
						<Select labelId="token-expiry" label="Expires after" value={expiry} onChange={(e) => setExpiry(e.target.value)}>
							{EXPIRY_OPTIONS.map((o) => (
								<MenuItem key={o.value} value={o.value}>
									{o.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
						The token acts as you: it can run the suites and tests you can run, follow their progress and stop them. It
						can't read or change anything else.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={onClose} disabled={saving}>
						Cancel
					</Button>
					<Button type="submit" variant="contained" disabled={saving || !name.trim()}>
						{saving ? "Creating…" : "Create token"}
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
}

function CreatedDialog({ created, onClose }) {
	if (!created) return null;
	return (
		<Dialog open onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle>Token “{created.apiToken.name}” created</DialogTitle>
			<DialogContent>
				<Alert severity="warning" sx={{ mb: 2 }}>
					Copy it now. You won't be able to see it again; if it's lost, revoke it and create a new one.
				</Alert>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
					<Box
						sx={{
							...mono,
							flex: 1,
							p: 1.25,
							fontSize: 13,
							borderRadius: 1,
							border: 1,
							borderColor: "divider",
							bgcolor: "action.hover",
							wordBreak: "break-all",
						}}
					>
						{created.value}
					</Box>
					<CopyButton text={created.value} />
				</Box>
				<Typography variant="subtitle1" fontWeight={600} gutterBottom>
					Set up your pipeline
				</Typography>
				<PipelineSetup />
			</DialogContent>
			<DialogActions>
				<Button variant="contained" onClick={onClose}>
					Done
				</Button>
			</DialogActions>
		</Dialog>
	);
}

export default function ApiTokens({ isAdmin }) {
	const { enqueueSnackbar } = useSnackbar();
	const [all, setAll] = useState(false);
	const [tokens, setTokens] = useState([]);
	const [loading, setLoading] = useState(true);
	const [creating, setCreating] = useState(false);
	const [created, setCreated] = useState(null);
	const [revoking, setRevoking] = useState(null);
	const [showSetup, setShowSetup] = useState(false);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			setTokens(all ? await tokenService.listAll() : await tokenService.listOwn());
		} catch (err) {
			enqueueSnackbar(errorMessage(err, "Couldn't load the tokens"), { variant: "error" });
		} finally {
			setLoading(false);
		}
	}, [all, enqueueSnackbar]);

	useEffect(() => {
		load();
	}, [load]);

	const revoke = async () => {
		try {
			await tokenService.revoke(revoking.id);
			enqueueSnackbar(`Token “${revoking.name}” revoked`, { variant: "success" });
			setRevoking(null);
			load();
		} catch (err) {
			enqueueSnackbar(errorMessage(err, "Couldn't revoke the token"), { variant: "error" });
		}
	};

	const expired = (t) => t.expiresAt && elapsedMs(t.expiresAt, Date.now()) > 0;

	return (
		<Card>
			<CardContent>
				<Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 2 }}>
					<Box sx={{ maxWidth: 640 }}>
						<Typography variant="h6" fontWeight={600} gutterBottom>
							API tokens
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Run suites and tests from a CI/CD pipeline (GitHub Actions, GitLab, Jenkins, Azure DevOps) with the Testify CLI. A
							token acts as you and can only start, follow and stop runs.{" "}
							<Link component="button" type="button" variant="body2" onClick={() => setShowSetup((s) => !s)} sx={{ verticalAlign: "baseline" }}>
								{showSetup ? "Hide pipeline setup" : "Show pipeline setup"}
							</Link>
						</Typography>
					</Box>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						{isAdmin && (
							<FormControlLabel
								control={<Switch size="small" checked={all} onChange={(e) => setAll(e.target.checked)} />}
								label="All users"
							/>
						)}
						<Button variant="contained" startIcon={<Add />} onClick={() => setCreating(true)}>
							New token
						</Button>
					</Box>
				</Box>

				{showSetup && (
					<Box sx={{ mb: 3, p: 2, border: 1, borderColor: "divider", borderRadius: 2 }}>
						<PipelineSetup />
					</Box>
				)}

				{loading ? (
					<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
						<CircularProgress size={28} />
					</Box>
				) : tokens.length === 0 ? (
					<Box sx={{ textAlign: "center", py: 5, color: "text.secondary" }}>
						<Key sx={{ fontSize: 40, opacity: 0.4, mb: 1 }} />
						<Typography variant="body2">{all ? "No one in your organisation has an API token." : "You have no API tokens yet."}</Typography>
					</Box>
				) : (
					<Box sx={{ overflowX: "auto" }}>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Name</TableCell>
									{all && <TableCell>Owner</TableCell>}
									<TableCell>Token</TableCell>
									<TableCell>Created</TableCell>
									<TableCell>Last used</TableCell>
									<TableCell>Expires</TableCell>
									<TableCell align="right" />
								</TableRow>
							</TableHead>
							<TableBody>
								{tokens.map((t) => (
									<TableRow key={t.id} hover>
										<TableCell sx={{ fontWeight: 500 }}>{t.name}</TableCell>
										{all && <TableCell>{t.ownerName}</TableCell>}
										<TableCell sx={{ ...mono, fontSize: 13, color: "text.secondary" }}>tfy_…{t.hint}</TableCell>
										<TableCell>{formatDate(t.createdAt)}</TableCell>
										<TableCell>{t.lastUsedAt ? timeAgo(t.lastUsedAt) : "Never"}</TableCell>
										<TableCell>
											{!t.expiresAt ? (
												"Never"
											) : expired(t) ? (
												<Chip size="small" color="error" variant="outlined" label="Expired" />
											) : (
												formatDate(t.expiresAt)
											)}
										</TableCell>
										<TableCell align="right">
											<Tooltip title="Revoke">
												<IconButton size="small" onClick={() => setRevoking(t)} aria-label={`Revoke ${t.name}`}>
													<DeleteOutline fontSize="small" />
												</IconButton>
											</Tooltip>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</Box>
				)}
			</CardContent>

			<CreateDialog
				open={creating}
				onClose={() => setCreating(false)}
				onCreated={(c) => {
					setCreating(false);
					setCreated(c);
					load();
				}}
			/>
			<CreatedDialog created={created} onClose={() => setCreated(null)} />

			<Dialog open={!!revoking} onClose={() => setRevoking(null)} maxWidth="xs" fullWidth>
				<DialogTitle>Revoke “{revoking?.name}”?</DialogTitle>
				<DialogContent>
					<Typography variant="body2">
						Pipelines using this token stop working at once{revoking?.ownerName ? ` (it belongs to ${revoking.ownerName})` : ""}. This
						can't be undone.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setRevoking(null)}>Cancel</Button>
					<Button color="error" variant="contained" onClick={revoke}>
						Revoke
					</Button>
				</DialogActions>
			</Dialog>
		</Card>
	);
}
